import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchDevices } from '@/entities/device/api';
import {
  isCameraLike,
  isTerminalLike,
  type DeviceSummary,
} from '@/entities/device/types';
import { fetchStreamCameras } from '@/entities/stream/api';
import {
  groupByRecorder,
  recorderDeviceIds,
} from '@/entities/stream/recorders';
import { matchesTerminal } from '@/entities/stream/terminal-match';
import { playableProfiles, type StreamCamera } from '@/entities/stream/types';
import type { IconName } from '@/shared/ui/icons';
import type { GroupBy } from './view-store';

/**
 * Камеры компании так, как их видит просмотр: кто чей, кто в сети, в каком порядке.
 *
 * Два источника, и ни один не полон сам по себе: каталог `/stream/cameras` знает
 * камеры и их потоки, список устройств — владельцев, их отделы и когда они были на
 * связи. Здесь они сводятся один раз — для дерева, для раскладки и для карточки
 * отдела, которая показывает «камеры этого отдела».
 */
export type TreeGroup = {
  key: string;
  title: string;
  subtitle?: string;
  icon: IconName;
  /** Узел-устройство в сети (регистратор). Опущено — группа, а не устройство. */
  online?: boolean;
  cameras: StreamCamera[];
  /** Настройки устройства-узла (регистратора); `sourceId` — его `peer`. */
  settings?: { sourceId: string; name: string };
  /** Пусто у регистратора, который ещё не прислал список камер. */
  emptyHint?: string;
};

export type CatalogLabels = {
  recorder: string;
  cameras: string;
  terminals: string;
  noChannels: string;
  noDepartment: string;
};

export type Catalog = {
  cameras: StreamCamera[];
  byId: Map<string, StreamCamera>;
  groups: TreeGroup[];
  /** Камеры компании, ни разу не выходившие на связь. */
  pending: DeviceSummary[];
  /** Камеры, которые можно включить сейчас, — в порядке дерева «по устройствам». */
  order: string[];
  /** Строка устройства, которой принадлежит камера: регистратор, камера, терминал. */
  ownerOf: (camera: StreamCamera) => DeviceSummary | undefined;
  /**
   * Собственная строка камеры в списке устройств. У канала регистратора она своя
   * (`<nvr>:<cameraId>`, nvr/cabinet.md §3) — отдельно от строки самого регистратора.
   */
  rowOf: (camera: StreamCamera) => DeviceSummary | undefined;
  /** Отдел камеры: свой у канала, а если не задан — регистратора (см. `departmentOf`). */
  departmentOf: (camera: StreamCamera) => Department | null;
};

export type Department = { id: string; name: string };

/**
 * Отдел камеры.
 *
 * У канала регистратора — своя строка устройства, и отдел пишется в неё: так камера
 * «вход» того же регистратора уходит в «Проходную», а «склад» — в «Склад» (владелец,
 * 2026-10-22). Отдел самого регистратора — значение по умолчанию для каналов, которым
 * свой не назначен: разложить шестнадцать камер, начав с «все — в цех», быстрее, чем
 * назначать каждую.
 */
export function departmentOfRows(
  row: DeviceSummary | undefined,
  owner: DeviceSummary | undefined,
): Department | null {
  const from = row?.department_id ? row : owner?.department_id ? owner : null;
  return from?.department_id
    ? {
        id: from.department_id,
        name: from.department_name || from.department_id,
      }
    : null;
}

/** Можно ли включить камеру прямо сейчас — те, что раскладываются сами. */
export function isWatchable(c: StreamCamera): boolean {
  return c.available && c.online === true && playableProfiles(c).length > 0;
}

function ownerIds(d: DeviceSummary): string[] {
  return [d.device_id, d.machine_number].filter((v): v is string => !!v);
}

/**
 * Свести каталог и устройства. `complete` — список устройств пришёл целиком; только
 * тогда камера без владельца считается удалённой и прячется: каталог какое-то время
 * помнит источник, которого в компании уже нет (владелец, 2026-10-22: «удалил
 * устройство, а оно всё ещё тут»).
 */
export function buildCatalog(
  all: readonly StreamCamera[],
  deviceRows: readonly DeviceSummary[],
  complete: boolean,
  groupBy: GroupBy,
  labels: CatalogLabels,
): Catalog {
  const owners = new Map<string, DeviceSummary>();
  for (const d of deviceRows) {
    for (const id of ownerIds(d)) {
      owners.set(id, d);
    }
  }
  const ownerOf = (c: StreamCamera): DeviceSummary | undefined =>
    owners.get(c.peer) ??
    deviceRows.find(d =>
      ownerIds(d).some(id => c.deviceId.startsWith(`${id}:`)),
    );

  const rows = new Map(deviceRows.map(d => [d.device_id, d]));
  const rowOf = (c: StreamCamera): DeviceSummary | undefined =>
    rows.get(c.deviceId);
  const departmentOf = (c: StreamCamera) =>
    departmentOfRows(rowOf(c), ownerOf(c));

  const cameras = complete ? all.filter(c => !!ownerOf(c)) : all.slice();
  const byId = new Map(cameras.map(c => [c.deviceId, c]));

  const terminals = deviceRows.filter(d => isTerminalLike(d.device_type));
  const isTerminalCamera = (c: StreamCamera) =>
    terminals.some(d => matchesTerminal(c, ownerIds(d), d.device_name));

  const { recorders, loose } = groupByRecorder(
    cameras,
    deviceRows as DeviceSummary[],
  );
  const byDevice: TreeGroup[] = recorders.map(r => ({
    key: `nvr:${r.peer}`,
    title: r.name,
    subtitle: [labels.recorder, r.machineNumber].filter(Boolean).join(' · '),
    icon: 'recorder',
    online: r.online,
    cameras: r.channels,
    settings: { sourceId: r.peer, name: r.name },
    emptyHint: labels.noChannels,
  }));
  const plain = loose.filter(c => !isTerminalCamera(c));
  const faceid = loose.filter(isTerminalCamera);
  if (plain.length > 0) {
    byDevice.push({
      key: 'cameras',
      title: labels.cameras,
      icon: 'camera',
      cameras: plain,
    });
  }
  if (faceid.length > 0) {
    byDevice.push({
      key: 'terminals',
      title: labels.terminals,
      icon: 'devices',
      cameras: faceid,
    });
  }
  const ordered = byDevice.flatMap(g => g.cameras);

  let groups = byDevice;
  if (groupBy === 'department') {
    const depts = new Map<string, TreeGroup>();
    for (const c of ordered) {
      const dept = departmentOf(c);
      const key = dept ? `dept:${dept.id}` : 'dept:none';
      if (!depts.has(key)) {
        depts.set(key, {
          key,
          title: dept ? dept.name : labels.noDepartment,
          icon: 'building',
          cameras: [],
        });
      }
      depts.get(key)!.cameras.push(c);
    }
    // Без отдела — последней группой: это то, что ещё предстоит разложить.
    groups = [...depts.values()].sort((a, b) =>
      a.key === 'dept:none'
        ? 1
        : b.key === 'dept:none'
        ? -1
        : a.title.localeCompare(b.title),
    );
  }

  const known = new Set(cameras.flatMap(c => [c.deviceId, c.peer]));
  const ofRecorders = recorderDeviceIds(deviceRows as DeviceSummary[]);
  const pending = deviceRows.filter(
    d =>
      isCameraLike(d.device_type) &&
      !known.has(d.device_id) &&
      !ofRecorders.has(d.device_id),
  );

  return {
    cameras,
    byId,
    groups,
    pending,
    order: ordered.filter(isWatchable).map(c => c.deviceId),
    ownerOf,
    rowOf,
    departmentOf,
  };
}

/** Камеры отдела — по своему отделу камеры, а без него — её регистратора. */
export function camerasOfDepartment(
  catalog: Catalog,
  departmentIds: readonly string[],
): StreamCamera[] {
  return catalog.cameras.filter(c => {
    const dept = catalog.departmentOf(c);
    return !!dept && departmentIds.includes(dept.id);
  });
}

/** Оба запроса и их сведение. Ключи общие с остальным кабинетом — кэш один. */
export function useCameraCatalog(
  companyId: string | undefined,
  groupBy: GroupBy,
  labels: CatalogLabels,
) {
  const query = useQuery({
    queryKey: ['stream-cameras', companyId],
    queryFn: () => fetchStreamCameras(companyId as string),
    enabled: !!companyId,
    // Кто вышел в сеть и кто пропал — без ручного обновления: экран держат открытым часами.
    refetchInterval: 30_000,
  });
  const devices = useQuery({
    // Свой ключ: вкладка терминалов кэширует тот же адрес с другими параметрами.
    queryKey: ['devices', 'camera-sources', companyId],
    queryFn: () => fetchDevices(companyId as string, { perPage: 100 }),
    enabled: !!companyId,
    refetchInterval: 60_000,
  });

  const { recorder, cameras, terminals, noChannels, noDepartment } = labels;
  const catalog = useMemo(() => {
    const rows = devices.data?.data ?? [];
    const total = devices.data?.meta?.total ?? rows.length;
    return buildCatalog(
      query.data?.data ?? [],
      rows,
      devices.isSuccess && rows.length >= total,
      groupBy,
      { recorder, cameras, terminals, noChannels, noDepartment },
    );
  }, [
    query.data,
    devices.data,
    devices.isSuccess,
    groupBy,
    recorder,
    cameras,
    terminals,
    noChannels,
    noDepartment,
  ]);

  return {
    catalog,
    isPending: query.isPending || devices.isPending,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
      void devices.refetch();
    },
  };
}
